import React, { useState } from 'react';
import { Card, Col, Row, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import salesConversionReportsService from '@/services/salesConversionReports.service';
import { defaultSalesReportFilters, SalesReportFilterBar } from './SalesReportFilterBar';

const { Title } = Typography;

const SalesWinLossPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const [filters, setFilters] = useState(defaultSalesReportFilters());
  const { data, isLoading } = useQuery({
    queryKey: ['sales-win-loss', filters],
    queryFn: () => salesConversionReportsService.winLoss(filters),
  });

  const kpis = (data?.kpis as Record<string, unknown>) || {};
  const breakdowns = (data?.breakdowns as Record<string, Array<Record<string, unknown>>>) || {};

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Win / loss analysis</Title>
      <SalesReportFilterBar value={filters} onChange={setFilters} />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card loading={isLoading}><Statistic title="Won" value={Number(kpis.won_count || 0)} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}><Statistic title="Lost" value={Number(kpis.lost_count || 0)} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Win rate" value={kpis.win_rate_pct == null ? '—' : `${kpis.win_rate_pct}%`} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic
              title="Δ vs prior (pp)"
              value={kpis.win_rate_change_pp == null ? '—' : String(kpis.win_rate_change_pp)}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card loading={isLoading}>
            <Statistic title="Won value" value={formatCurrency(Number(kpis.won_value || 0))} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card loading={isLoading}>
            <Statistic title="Lost value" value={formatCurrency(Number(kpis.lost_value || 0))} />
          </Card>
        </Col>
      </Row>
      <Card title="By reason" style={{ marginBottom: 16 }}>
        <Table
          loading={isLoading}
          size="small"
          rowKey={(r) => String(r.reason)}
          dataSource={breakdowns.by_reason || []}
          columns={[
            { title: 'Reason', dataIndex: 'reason' },
            { title: 'Won', dataIndex: 'won', align: 'right' },
            { title: 'Lost', dataIndex: 'lost', align: 'right' },
            { title: 'Value', dataIndex: 'value', align: 'right', render: (v) => formatCurrency(Number(v || 0)) },
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      <Card title="By sales owner" style={{ marginBottom: 16 }}>
        <Table
          loading={isLoading}
          size="small"
          rowKey={(r) => String(r.owner_id || r.sales_owner)}
          dataSource={breakdowns.by_sales_owner || []}
          columns={[
            { title: 'Owner', dataIndex: 'sales_owner' },
            { title: 'Won', dataIndex: 'won', align: 'right' },
            { title: 'Lost', dataIndex: 'lost', align: 'right' },
            { title: 'Value', dataIndex: 'value', align: 'right', render: (v) => formatCurrency(Number(v || 0)) },
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      <Card title="By industry">
        <Table
          loading={isLoading}
          size="small"
          rowKey={(r) => String(r.industry)}
          dataSource={breakdowns.by_industry || []}
          columns={[
            { title: 'Industry', dataIndex: 'industry' },
            { title: 'Won', dataIndex: 'won', align: 'right' },
            { title: 'Lost', dataIndex: 'lost', align: 'right' },
            { title: 'Value', dataIndex: 'value', align: 'right', render: (v) => formatCurrency(Number(v || 0)) },
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default SalesWinLossPage;
